/**
 * Dados mocados do modulo "Canais" (avisos).
 *
 * No protótipo esses canais tem `kind: 'Canal'` e ficam fora da Comunicacao
 * (`docs/design-slices/_shell.html:2753`); todos sao somente leitura e apenas
 * administradores publicam (`_shell.html:2974`). Os chips do explorer sao
 * "Seguindo" e "Explorar" (`_shell.html:2288`).
 *
 * Mesma referencia temporal de `mocks/chat.ts`: 6 de agosto de 2026.
 */
import type { ChatChannel, ChatMessage } from './chat.ts'

export type BroadcastChannel = ChatChannel & {
  /** A pessoa logada segue o canal — separa os chips "Seguindo"/"Explorar". */
  following: boolean
  followerCount: number
  /** Quem publica, mostrado no cabecalho da conversa. */
  publisher: string
}

export const MOCK_BROADCASTS: readonly BroadcastChannel[] = [
  {
    id: 'bc-avisos-gerais',
    name: 'Wynk · Avisos gerais',
    kind: 'time',
    description: 'Comunicados oficiais da direção.',
    icon: 'fas fa-bullhorn',
    members: ['Direção Wynk'],
    memberCount: 42,
    unread: 2,
    mentionsMe: false,
    lastMessagePreview: 'Direção Wynk: Nova política de horário de foco.',
    lastMessageAt: '09:00',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: true,
    following: true,
    followerCount: 42,
    publisher: 'Direção Wynk',
  },
  {
    id: 'bc-novidades-produto',
    name: 'Orbind · Novidades do produto',
    kind: 'produto',
    description: 'O que muda a cada entrega da plataforma.',
    icon: 'fas fa-rocket',
    members: ['Time Orbind'],
    memberCount: 38,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Time Orbind: O explorer agora pode ser redimensionado e recolhido.',
    lastMessageAt: 'Ontem',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: true,
    following: true,
    followerCount: 38,
    publisher: 'Time Orbind',
  },
  {
    id: 'bc-rh',
    name: 'RH · Comunicados',
    kind: 'time',
    description: 'Avisos de pessoas e cultura.',
    icon: 'fas fa-id-badge',
    members: ['RH Wynk'],
    memberCount: 42,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'RH Wynk: Férias coletivas de fim de ano até 30/09.',
    lastMessageAt: '04/08',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: true,
    following: true,
    followerCount: 42,
    publisher: 'RH Wynk',
  },
  {
    id: 'bc-seguranca',
    name: 'Segurança da informação',
    kind: 'time',
    description: 'Alertas e boas práticas de segurança.',
    icon: 'fas fa-shield-alt',
    members: ['Segurança Wynk'],
    memberCount: 19,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Segurança Wynk: Revisão trimestral de acessos começa na segunda.',
    lastMessageAt: '01/08',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: true,
    following: false,
    followerCount: 19,
    publisher: 'Segurança Wynk',
  },
  {
    id: 'bc-empresas',
    name: 'Vozes do cliente',
    kind: 'empresa',
    description: 'Resumo semanal do que os empresas têm pedido.',
    icon: 'fas fa-comment-dots',
    members: ['Atendimento Wynk'],
    memberCount: 12,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Atendimento Wynk: Resumo da semana — 14 pedidos viraram demandas.',
    lastMessageAt: '31/07',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: true,
    following: false,
    followerCount: 12,
    publisher: 'Atendimento Wynk',
  },
]

export const MOCK_BROADCAST_MESSAGES: Record<string, readonly ChatMessage[]> = {
  'bc-avisos-gerais': [
    { id: 'b-ag-01', kind: 'date', text: 'Hoje' },
    {
      id: 'b-ag-02',
      kind: 'message',
      author: 'Direção Wynk',
      initials: 'DW',
      text: 'A partir desta semana, toda demanda deve entrar pela Orbind — e-mails e planilhas deixam de valer como registro oficial.',
      time: '08:40',
      mine: false,
      read: false,
    },
    {
      id: 'b-ag-03',
      kind: 'message',
      author: 'Direção Wynk',
      initials: 'DW',
      text: 'Nova política de horário de foco: bloqueios de agenda passam a ser respeitados pelas automações de notificação.',
      time: '09:00',
      mine: false,
      read: false,
    },
  ],
  'bc-novidades-produto': [
    { id: 'b-np-01', kind: 'date', text: 'Ontem' },
    {
      id: 'b-np-02',
      kind: 'message',
      author: 'Time Orbind',
      initials: 'TO',
      text: 'Lançamos a visão em árvore nas Demandas e nos Itens de Trabalho.',
      time: '17:12',
      mine: false,
      read: true,
    },
    {
      id: 'b-np-03',
      kind: 'message',
      author: 'Time Orbind',
      initials: 'TO',
      text: 'O explorer agora pode ser redimensionado e recolhido.',
      time: '17:30',
      mine: false,
      read: true,
    },
  ],
  'bc-rh': [
    { id: 'b-rh-01', kind: 'date', text: 'Terça-feira, 4 de agosto' },
    {
      id: 'b-rh-02',
      kind: 'message',
      author: 'RH Wynk',
      initials: 'RW',
      text: 'As férias coletivas de fim de ano serão registradas na Orbind até 30/09. Dúvidas no canal de Operações.',
      time: '10:00',
      mine: false,
      read: true,
    },
  ],
  'bc-seguranca': [
    { id: 'b-sg-01', kind: 'date', text: 'Sábado, 1 de agosto' },
    {
      id: 'b-sg-02',
      kind: 'message',
      author: 'Segurança Wynk',
      initials: 'SW',
      text: 'Revisão trimestral de acessos começa na segunda. Cada gestor recebe a lista da sua área.',
      time: '09:30',
      mine: false,
      read: true,
    },
  ],
  'bc-empresas': [
    { id: 'b-cl-01', kind: 'date', text: 'Sexta-feira, 31 de julho' },
    {
      id: 'b-cl-02',
      kind: 'message',
      author: 'Atendimento Wynk',
      initials: 'AW',
      text: 'Resumo da semana — 14 pedidos de empresas viraram demandas; 9 já estão em execução.',
      time: '18:05',
      mine: false,
      read: true,
    },
  ],
}
